import { useState } from 'react';
import { api, ApiError } from '../api.ts';
import { BriefingForm } from './BriefingForm.tsx';
import { BriefingView } from './BriefingView.tsx';
import { briefingPayload, clockOf, draftFrom, type BriefingDraft, type BriefingView as Briefing } from '../briefing.ts';

const ERRORS: Record<string, string> = {
  INVALID_SCHEDULE: 'Une étape du planning est invalide (elle doit tomber pendant la mission).',
  INVALID_CHECKLIST: 'Un point de la check-list est invalide.',
  INVALID_BREAK: 'La pause doit durer entre 0 et 240 minutes.',
  INVALID_BRIEFING_TIME: 'Le brief doit être placé entre 0 et 12 heures avant le début.',
  INVALID_BRIEFING_TEXT: 'Un des textes est trop long.',
  BRIEFING_LOCKED: 'La mission est terminée : la fiche ne peut plus être modifiée.',
  TEMPLATE_NAME_TAKEN: 'Un modèle porte déjà ce nom.',
  TOO_MANY_TEMPLATES: 'Nombre maximal de modèles atteint : supprimez-en un.',
};

/**
 * Fiche pratique côté restaurateur : lecture, modification tant que la mission n'est pas terminée,
 * et enregistrement de la mission comme modèle réutilisable.
 */
export function BriefingPanel({ missionId, organizationId, startsAt, briefing, editable, onChanged }: {
  missionId: string;
  organizationId: string;
  startsAt: string;
  briefing: Briefing;
  editable: boolean;
  onChanged: () => Promise<void> | void;
}) {
  const [draft, setDraft] = useState<BriefingDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const startClock = clockOf(startsAt);

  async function run(action: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      await onChanged();
      setMessage({ kind: 'ok', text: ok });
      return true;
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof ApiError ? ERRORS[err.code] ?? 'Enregistrement impossible.' : 'Enregistrement impossible.' });
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!draft) return;
    if (await run(() => api.put(`/missions/${missionId}/briefing`, briefingPayload(draft, startClock)), 'Fiche enregistrée.')) setDraft(null);
  }

  function saveAsTemplate() {
    const name = window.prompt('Nom du modèle (ex. « Banquet 80 couverts ») :');
    if (name?.trim()) void run(() => api.post('/missions/templates', { organizationId, name: name.trim(), fromMissionId: missionId }), 'Modèle enregistré : retrouvez-le à la création d’une mission.');
  }

  return (
    <>
      {message && (message.kind === 'ok' ? <div className="badge badge-available" style={{ padding: '10px 14px', whiteSpace: 'normal' }}>{message.text}</div> : <div className="error-banner">{message.text}</div>)}

      {draft ? (
        <>
          <BriefingForm value={draft} onChange={setDraft} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Enregistrement…' : 'Enregistrer la fiche'}</button>
            <button className="btn btn-secondary" disabled={busy} onClick={() => setDraft(null)}>Annuler</button>
          </div>
        </>
      ) : (
        <>
          <BriefingView briefing={briefing} startsAt={startsAt} audience="org" />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {editable && (
              <button className="btn btn-secondary btn-sm" onClick={() => setDraft(draftFrom(briefing, startClock, briefing.private))}>
                {briefing.dressCode || briefing.schedule.length || briefing.checklist.length ? 'Modifier la fiche pratique' : 'Compléter la fiche pratique'}
              </button>
            )}
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={saveAsTemplate}>Enregistrer comme modèle</button>
          </div>
        </>
      )}
    </>
  );
}
